import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, CircularProgress, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Layout from '../../components/layout/Layout';
import PrimarySearchBar from '../../components/searchBar/searchbar';
import { getSeasonalMenus, addSeasonalMenu, updateSeasonalMenu, deleteSeasonalMenu, getSeasonalOrders, upsertSeasonalOrder, updateOrderStatus, seasonalFlagOff } from '../../Redux/Actions/seasonalMenuAction';
import { getStores } from '../../Redux/Actions/storeAction';
import SeasonalTabs from '../seasonal/SeasonalTabs';
import './seasonalmenu.css';
import { MENU_STATUS, ORDER_STATUS, fmtDate, fmtPrice, StatusBadge } from './seasonalMenuShared';
import CampaignMenuDialog from './CampaignMenuDialog';
import OrderEditDialog from './OrderEditDialog';

// ─── Main Component ───────────────────────────────────────────────────────────

function SeasonalMenu({ user }) {
  const dispatch = useDispatch();
  const { menus, orders, isMenuAdded, isMenuUpdated, isMenuDeleted, isSent } =
    useSelector(s => s.SeasonalMenu);
  const { stores } = useSelector(s => s.Store);

  // left panel state
  const [textVal, setTextVal] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // dialogs
  const [menuDialog, setMenuDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [orderDialog, setOrderDialog] = useState(false);
  const [orderDialogStore, setOrderDialogStore] = useState(null);
  const [orderDialogExisting, setOrderDialogExisting] = useState(null);

  // filter
  const [statusFilter, setStatusFilter] = useState('all');

  // 가격 확인 현황
  const [confirmStats, setConfirmStats] = useState(null);
  const [confirmStatsLoading, setConfirmStatsLoading] = useState(false);
  const [sendChannel, setSendChannel] = useState('email');
  const [smsCustomText, setSmsCustomText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { sent, skipped, errors }
  const [confirmFilter, setConfirmFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [sendMenuAnchor, setSendMenuAnchor] = useState(null);
  const [sendMode, setSendMode] = useState('all'); // 'all' | 'selected' | 'individual'
  const [individualSendStoreId, setIndividualSendStoreId] = useState(null);
  const [sendTableSelectedIds, setSendTableSelectedIds] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtmlDraft, setEmailHtmlDraft] = useState('');
  const [sendTemplateLoading, setSendTemplateLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPriceInputs, setBulkPriceInputs] = useState({}); // { itemName: { s, l, single } }
  const [bulkPriceSaving, setBulkPriceSaving] = useState(false);

  // ── effects ──
  useEffect(() => {
    if (user?._id) {
      dispatch(getSeasonalMenus(user._id));
      dispatch(getStores(user._id));
    }
  }, [user]);
  useEffect(() => { setFiltered([...menus]); }, [menus]);
  useEffect(() => {
    if (isMenuAdded || isMenuUpdated) {
      dispatch(seasonalFlagOff());
      setMenuDialog(false);
      setEditTarget(null);
    }
    if (isMenuDeleted || isSent) dispatch(seasonalFlagOff());
  }, [isMenuAdded, isMenuUpdated, isMenuDeleted, isSent]);

  useEffect(() => {
    if (selectedMenu) {
      dispatch(getSeasonalOrders(selectedMenu._id));
      setStatusFilter('all');
      setConfirmFilter('all');
      setSelectedOrders([]);
      setSendTableSelectedIds([]);
      fetchConfirmStats(selectedMenu._id);
    }
  }, [selectedMenu]);

  const fetchConfirmStats = async (menuId) => {
    setConfirmStatsLoading(true);
    try {
      const { api } = await import('../../api/api');
      const { data } = await api.get(`/seasonal/confirm-status/${menuId}`);
      setConfirmStats(data);
    } catch (_) { setConfirmStats(null); }
    setConfirmStatsLoading(false);
  };

  // keep selectedMenu in sync when menus list updates
  useEffect(() => {
    if (selectedMenu) {
      const updated = menus.find(m => m._id === selectedMenu._id);
      if (updated) setSelectedMenu(updated);
    }
  }, [menus]);

  // ── search ──
  const handleSearch = (val) => {
    setTextVal(val);
    const q = (val || '').toLowerCase().trim();
    if (!q) {
      setFiltered([...menus]);
      return;
    }
    const itemMatch = (m) => (m.items || []).some((it) => (it.name || '').toLowerCase().includes(q));
    setFiltered(menus.filter((m) =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.description || '').toLowerCase().includes(q) ||
      itemMatch(m)
    ));
  };

  // ── campaign CRUD ──
  const openAdd = () => { setEditTarget(null); setMenuDialog(true); };
  const openEdit = (m) => { setEditTarget(m); setMenuDialog(true); };

  const handleMenuSubmit = (obj) => {
    if (editTarget) dispatch(updateSeasonalMenu(editTarget._id, { ...obj, user: user._id }));
    else dispatch(addSeasonalMenu({ ...obj, user: user._id }));
  };

  const loadSendTemplate = async (menuId) => {
    setSendTemplateLoading(true);
    try {
      const { api } = await import('../../api/api');
      const { data } = await api.get(`/seasonal/send-template/${menuId}`);
      setEmailSubject(data.defaultSubject || '');
      setEmailHtmlDraft(data.htmlTemplate || '');
    } catch (_) {
      setEmailSubject('');
      setEmailHtmlDraft('');
    }
    setSendTemplateLoading(false);
  };

  const openSendDialog = async (mode, storeIdForIndividual = null) => {
    if (!selectedMenu?._id) return;
    if (mode === 'selected' && sendTableSelectedIds.length === 0) return;
    setSendMode(mode);
    setIndividualSendStoreId(storeIdForIndividual);
    setSendConfirm(selectedMenu._id);
    setSendResult(null);
    setSendMenuAnchor(null);
    await loadSendTemplate(selectedMenu._id);
  };

  const handleSend = async (menuId) => {
    let storeIds;
    if (sendMode === 'all') storeIds = undefined;
    else if (sendMode === 'individual') {
      if (!individualSendStoreId) {
        setSendResult({ error: '개별 발송 대상 점포가 없습니다.' });
        return;
      }
      storeIds = [individualSendStoreId];
    } else {
      storeIds = [...sendTableSelectedIds];
      if (storeIds.length === 0) {
        setSendResult({ error: '선택한 점포가 없습니다.' });
        return;
      }
    }

    setIsSending(true);
    setSendResult(null);
    try {
      const { api } = await import('../../api/api');
      const body = {
        menuId,
        userId: user._id,
        channel: sendChannel,
        ...(smsCustomText.trim() ? { smsText: smsCustomText.trim() } : {}),
        ...(Array.isArray(storeIds) && storeIds.length ? { storeIds } : {}),
      };
      if (emailSubject.trim()) body.emailSubject = emailSubject.trim();
      if (emailHtmlDraft.trim()) body.emailHtml = emailHtmlDraft.trim();

      const { data } = await api.post('/seasonal/send', body);
      setSendResult(data);
      dispatch(seasonalFlagOff());
      setSendTableSelectedIds([]);
      setTimeout(() => fetchConfirmStats(menuId), 1500);
    } catch (err) {
      setSendResult({ error: err.response?.data?.message || '발송 중 오류가 발생했습니다.' });
    } finally {
      setIsSending(false);
    }
  };

  // ── order status ──
  const handleOrderStatus = (orderId, status, store) => {
    const email = store?.ownerEmail || store?.managerEmail || '';
    dispatch(updateOrderStatus(orderId, status, email));
  };

  // ── order edit dialog ──
  const openOrderDialog = (store, existingOrder) => {
    setOrderDialogStore(store);
    setOrderDialogExisting(existingOrder || null);
    setOrderDialog(true);
  };

  const handleOrderSave = ({ participating, itemPrices, memo }) => {
    const storeId = orderDialogStore?._id;
    const menuId = selectedMenu?._id;
    if (!storeId || !menuId) return;
    dispatch(upsertSeasonalOrder({ storeId, menuId, participating, itemPrices, memo }));
    setOrderDialog(false);
  };

  // ── computed ──
  const activeStores = (stores || []).filter(s => s.status === 'active');
  const activeStoreCount = activeStores.length;

  const sendTargetCount =
    sendMode === 'all'
      ? activeStoreCount
      : sendMode === 'individual'
        ? (individualSendStoreId ? 1 : 0)
        : sendTableSelectedIds.length;

  // join stores with orders for the table
  const tableRows = activeStores.map(store => {
    const order = orders.find(o => {
      const sid = o.store?._id || o.store;
      return String(sid) === String(store._id);
    });
    return { store, order };
  });

  // stats
  const statCounts = {
    all: tableRows.length,
    pending: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'pending').length,
    confirmed: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'confirmed').length,
    rejected: tableRows.filter(r => r.order && r.order.participating !== false && r.order.status === 'rejected').length,
    noResponse: tableRows.filter(r => !r.order).length,
    noSale: tableRows.filter(r => r.order && r.order.participating === false).length,
  };

  // filtered rows
  const visibleRows = tableRows.filter(({ order }) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'noResponse') return !order;
    if (statusFilter === 'noSale') return order && order.participating === false;
    if (!order || order.participating === false) return false;
    return order.status === statusFilter;
  });

  const visibleStoreIds = visibleRows.map(r => String(r.store._id));
  const allSendVisibleChecked =
    visibleStoreIds.length > 0 && visibleStoreIds.every(id => sendTableSelectedIds.includes(id));
  const toggleSendAllVisible = () => {
    if (allSendVisibleChecked) {
      setSendTableSelectedIds(prev => prev.filter(id => !visibleStoreIds.includes(id)));
    } else {
      setSendTableSelectedIds(prev => [...new Set([...prev, ...visibleStoreIds])]);
    }
  };
  const toggleSendOneStore = (id) => {
    const sid = String(id);
    setSendTableSelectedIds(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  // items from selected menu
  const menuItems = selectedMenu?.items || [];

  return (
    <Layout title="Seasonal Menu" user={user}>
      <Box sx={{ p: 2 }}>
        <SeasonalTabs />
        <div className="seasonal-layout">

          {/* ── LEFT PANEL ── */}
          <div className="seasonal-left">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#551FFF', fontSize: 14 }}>
                시즌 캠페인
              </Typography>
              <button className="sm-btn sm-btn--primary" onClick={openAdd}>
                <AddIcon sx={{ fontSize: 13 }} /> 시즌 추가
              </button>
            </Box>

            <Box sx={{ mb: 1.5, background: 'white', borderRadius: '12px', px: 1 }}>
              <PrimarySearchBar value={textVal} onChange={e => handleSearch(e.target.value)} />
            </Box>
            {textVal && (
              <button
                onClick={() => handleSearch('')}
                style={{ marginBottom: 8, padding: '4px 12px', border: 'none', borderRadius: 8, background: '#eee', cursor: 'pointer', fontSize: 12 }}
              >
                초기화
              </button>
            )}

            {filtered.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: '#aaa', background: 'white', borderRadius: 2, fontSize: 13 }}>
                등록된 캠페인이 없습니다.
              </Box>
            )}

            {filtered.map(m => {
              const mOrders = orders; // we only have orders for selected, used for stats on selected
              const itemCount = (m.items || []).length;
              const isSelected = selectedMenu?._id === m._id;

              return (
                <div
                  key={m._id}
                  className={`campaign-card${isSelected ? ' campaign-card--selected' : ''}`}
                  onClick={() => setSelectedMenu(m)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                    <div className="campaign-card-name">{m.name}</div>
                    <StatusBadge map={MENU_STATUS} value={m.status} />
                  </div>
                  <div className="campaign-card-meta">
                    항목 {itemCount}개
                    {m.startDate && (
                      <> · {m.startDate.substring(0, 10)} ~ {m.endDate?.substring(0, 10)}</>
                    )}
                  </div>
                  {m.description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {m.description}
                    </div>
                  )}
                  {isSelected && statCounts.all > 0 && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
                      응답 {statCounts.all - statCounts.noResponse}/{statCounts.all}
                    </div>
                  )}
                  {m.sentAt && (
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                      발송: {fmtDate(m.sentAt)} · {m.sentCount}명
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="seasonal-right">
            {!selectedMenu ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280, background: 'white', borderRadius: 3, color: '#bbb' }}>
                <Typography>← 왼쪽에서 캠페인을 선택하면 현황이 표시됩니다</Typography>
              </Box>
            ) : (
              <Box>
                {/* ── HEADER ── */}
                <Box sx={{ background: 'white', borderRadius: 3, p: 2, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                          {selectedMenu.name}
                        </Typography>
                        <StatusBadge map={MENU_STATUS} value={selectedMenu.status} />
                      </Box>
                      {(selectedMenu.startDate || selectedMenu.endDate) && (
                        <Typography variant="caption" sx={{ color: '#888', mt: 0.3, display: 'block' }}>
                          {fmtDate(selectedMenu.startDate)} ~ {fmtDate(selectedMenu.endDate)}
                        </Typography>
                      )}
                      {selectedMenu.description && (
                        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                          {selectedMenu.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="sm-btn sm-btn--send"
                        disabled={activeStoreCount === 0}
                        onClick={(e) => setSendMenuAnchor(e.currentTarget)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
                      >
                        <SendIcon sx={{ fontSize: 13 }} /> 발송 <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />
                      </button>
                      <Menu
                        anchorEl={sendMenuAnchor}
                        open={Boolean(sendMenuAnchor)}
                        onClose={() => setSendMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      >
                        <MenuItem
                          onClick={() => openSendDialog('all')}
                          disabled={activeStoreCount === 0}
                        >
                          전체 발송 ({activeStoreCount}개 점포)
                        </MenuItem>
                        <MenuItem
                          onClick={() => openSendDialog('selected')}
                          disabled={sendTableSelectedIds.length === 0}
                        >
                          선택 발송 ({sendTableSelectedIds.length}개 선택됨)
                        </MenuItem>
                      </Menu>
                      <button className="sm-btn sm-btn--edit" onClick={() => openEdit(selectedMenu)}>
                        <EditIcon sx={{ fontSize: 13 }} /> 수정
                      </button>
                      <button className="sm-btn sm-btn--delete" onClick={() => setDeleteConfirm(selectedMenu._id)}>
                        <DeleteOutlineIcon sx={{ fontSize: 13 }} /> 삭제
                      </button>
                    </Box>
                  </Box>

                  {/* ── ITEM CHIPS ── */}
                  {menuItems.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                      {menuItems.map((item, idx) => (
                        <div key={idx} className="item-chip">
                          <span style={{ fontWeight: 600 }}>{item.name}</span>
                          {item.hot12Price > 0 && (
                            <span style={{ marginLeft: 6, color: '#ff6b35', fontWeight: 700, fontSize: 11 }}>
                              HOT {fmtPrice(item.hot12Price)}
                              {item.hot16Price > 0 && `/${fmtPrice(item.hot16Price)}`}
                            </span>
                          )}
                          {item.iced16Price > 0 && (
                            <span style={{ marginLeft: 4, color: '#0ea5e9', fontWeight: 700, fontSize: 11 }}>
                              ICED {fmtPrice(item.iced16Price)}
                            </span>
                          )}
                          {!item.hot12Price && !item.iced16Price && item.basePrice > 0 && (
                            <span style={{ marginLeft: 6, color: '#551FFF', fontWeight: 700 }}>
                              {fmtPrice(item.basePrice)}
                            </span>
                          )}
                        </div>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* ── STATS BAR ── */}
                <div className="campaign-stats-bar">
                  <button
                    className={`filter-btn${statusFilter === 'all' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('all')}
                  >
                    전체 <span style={{ fontWeight: 800 }}>{statCounts.all}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'pending' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('pending')}
                    style={{ color: '#f57f17' }}
                  >
                    신청대기 <span style={{ fontWeight: 800 }}>{statCounts.pending}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'confirmed' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('confirmed')}
                    style={{ color: '#2e7d32' }}
                  >
                    승인 <span style={{ fontWeight: 800 }}>{statCounts.confirmed}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'rejected' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('rejected')}
                    style={{ color: '#c62828' }}
                  >
                    거절 <span style={{ fontWeight: 800 }}>{statCounts.rejected}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'noResponse' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('noResponse')}
                    style={{ color: '#999' }}
                  >
                    미응답 <span style={{ fontWeight: 800 }}>{statCounts.noResponse}</span>
                  </button>
                  <button
                    className={`filter-btn${statusFilter === 'noSale' ? ' filter-btn--active' : ''}`}
                    onClick={() => setStatusFilter('noSale')}
                    style={{ color: '#c62828' }}
                  >
                    판매안함 <span style={{ fontWeight: 800 }}>{statCounts.noSale}</span>
                  </button>
                </div>

                {/* ── STORE TABLE ── */}
                <TableContainer
                  component={Paper}
                  sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', mt: 1.5 }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#f5f3ff' }}>
                        <TableCell padding="checkbox" sx={{ width: 40 }}>
                          <Tooltip title="현재 필터에 보이는 점포 전체 선택 → 발송 메뉴에서 선택 발송">
                            <input
                              type="checkbox"
                              checked={allSendVisibleChecked}
                              onChange={toggleSendAllVisible}
                              disabled={visibleStoreIds.length === 0}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>점포</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>판매여부</TableCell>
                        {menuItems.map((item, idx) => (
                          <TableCell key={idx} sx={{ fontWeight: 700, minWidth: 110 }}>
                            {item.name}
                            <br />
                            <span style={{ fontSize: 10, color: '#aaa', fontWeight: 400 }}>
                              기준 {fmtPrice(item.basePrice)}
                            </span>
                          </TableCell>
                        ))}
                        <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>메모</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 80 }}>상태</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeStores.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6 + menuItems.length} align="center" sx={{ py: 4, color: '#aaa' }}>
                            운영 중인 점포가 없습니다. Store 페이지에서 점포를 추가하세요.
                          </TableCell>
                        </TableRow>
                      )}
                      {visibleRows.map(({ store, order }) => {
                        const isNoResponse = !order;
                        const isNoSale = order && order.participating === false;

                        let rowBg = 'white';
                        if (isNoResponse) rowBg = '#fafafa';
                        else if (isNoSale) rowBg = '#fff5f5';
                        else if (order?.status === 'confirmed') rowBg = '#f1fff4';
                        else if (order?.status === 'rejected') rowBg = '#fff5f5';

                        return (
                          <TableRow
                            key={store._id}
                            hover
                            sx={{ background: rowBg }}
                          >
                            <TableCell padding="checkbox">
                              <input
                                type="checkbox"
                                checked={sendTableSelectedIds.includes(String(store._id))}
                                onChange={() => toggleSendOneStore(store._id)}
                                style={{ width: 16, height: 16, cursor: 'pointer' }}
                              />
                            </TableCell>
                            {/* 점포 */}
                            <TableCell>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {store.tljNo || store.ownerName || '-'}
                              </div>
                              <div style={{ fontSize: 11, color: '#888' }}>{store.address}</div>
                            </TableCell>

                            {/* 판매여부 */}
                            <TableCell>
                              {isNoResponse ? (
                                <span className="no-response">미응답</span>
                              ) : isNoSale ? (
                                <Chip label="판매안함" size="small" sx={{ background: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: 11 }} />
                              ) : (
                                <Chip label="판매" size="small" sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: 11 }} />
                              )}
                            </TableCell>

                            {/* 아이템별 판매가 */}
                            {menuItems.map((item, idx) => {
                              if (isNoResponse || isNoSale) {
                                return (
                                  <TableCell key={idx}>
                                    <span className="price-base">-</span>
                                  </TableCell>
                                );
                              }
                              const ip = (order?.itemPrices || []).find(p => p.itemName === item.name);
                              if (!ip) {
                                return (
                                  <TableCell key={idx}>
                                    <span className="price-base">{fmtPrice(item.basePrice)}</span>
                                  </TableCell>
                                );
                              }
                              const isChanged = Number(ip.finalPrice) !== Number(ip.basePrice);
                              return (
                                <TableCell key={idx}>
                                  <span className={isChanged ? 'price-changed' : 'price-base'}>
                                    {fmtPrice(ip.finalPrice)}
                                  </span>
                                  {isChanged && (
                                    <div style={{ fontSize: 10, color: '#aaa' }}>
                                      기준 {fmtPrice(ip.basePrice)}
                                    </div>
                                  )}
                                </TableCell>
                              );
                            })}

                            {/* 메모 */}
                            <TableCell sx={{ maxWidth: 140 }}>
                              <div style={{ fontSize: 12, color: '#555', wordBreak: 'break-all' }}>
                                {order?.memo || '-'}
                              </div>
                            </TableCell>

                            {/* 상태 */}
                            <TableCell>
                              {isNoResponse ? (
                                <span className="no-response" style={{ fontSize: 12 }}>미응답</span>
                              ) : isNoSale ? (
                                <span style={{ fontSize: 12, color: '#c62828' }}>판매안함</span>
                              ) : (
                                <StatusBadge map={ORDER_STATUS} value={order.status} />
                              )}
                            </TableCell>

                            {/* 관리 */}
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Tooltip title="이 점포에만 시즌 안내 메일·SMS 발송">
                                  <button
                                    type="button"
                                    className="order-action-btn"
                                    style={{ borderColor: '#2e7d32', color: '#2e7d32' }}
                                    onClick={() => openSendDialog('individual', String(store._id))}
                                  >
                                    <SendIcon sx={{ fontSize: 13 }} /> 개별
                                  </button>
                                </Tooltip>
                                {!isNoResponse && !isNoSale && order?.status !== 'confirmed' && (
                                  <Tooltip title="승인 + 이메일 발송">
                                    <button
                                      className="order-action-btn order-action-btn--approve"
                                      onClick={() => handleOrderStatus(order._id, 'confirmed', store)}
                                    >
                                      <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> 승인
                                    </button>
                                  </Tooltip>
                                )}
                                {!isNoResponse && !isNoSale && order?.status !== 'rejected' && (
                                  <Tooltip title="거절 + 이메일 발송">
                                    <button
                                      className="order-action-btn order-action-btn--reject"
                                      onClick={() => handleOrderStatus(order._id, 'rejected', store)}
                                    >
                                      <CancelOutlinedIcon sx={{ fontSize: 13 }} /> 거절
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip title={isNoResponse ? '새 신청 등록' : '수정'}>
                                  <button
                                    className="order-action-btn order-action-btn--edit"
                                    onClick={() => openOrderDialog(store, order)}
                                  >
                                    <EditIcon sx={{ fontSize: 13 }} /> 수정
                                  </button>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {visibleRows.length === 0 && activeStores.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6 + menuItems.length} align="center" sx={{ py: 3, color: '#aaa' }}>
                            해당 필터에 해당하는 점포가 없습니다.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 1, fontSize: 11, color: '#aaa' }}>
                  * 왼쪽 체크 후 발송 → 선택 발송. 행의 「개별」은 해당 점포만 발송합니다.
                  <br />
                  * 승인/거절 시 점주 이메일로 자동 알림이 발송됩니다.
                </Box>

                {/* ── 가격 확인 현황 ── */}
                {(() => {
                  // 액션 타입 계산 헬퍼
                  const getActionType = (order) => {
                    if (!order.priceConfirmedAt) return 'pending';
                    if (order.participating === false) return 'hold';
                    const hasPriceChange = (order.itemPrices || []).some(ip => {
                      if (ip.price_s != null && ip.finalPrice_s != null && ip.price_s !== ip.finalPrice_s) return true;
                      if (ip.price_l != null && ip.finalPrice_l != null && ip.price_l !== ip.finalPrice_l) return true;
                      if (ip.basePrice != null && ip.finalPrice != null && ip.basePrice !== ip.finalPrice) return true;
                      return false;
                    });
                    return hasPriceChange ? 'pricechange' : 'accept';
                  };

                  const ACTION_META = {
                    accept:      { label: '✅ 가격 수락',     bg: '#e8f5e9', color: '#2e7d32' },
                    hold:        { label: '⏸ 판매 보류',     bg: '#f5f5f5', color: '#616161' },
                    pricechange: { label: '✏️ 수정 요청',    bg: '#fff3e0', color: '#e65100' },
                    pending:     { label: '⏳ 미확인',        bg: '#fff8e1', color: '#f57f17' },
                  };

                  const allOrders = confirmStats?.orders || [];
                  // 필터 카운트
                  const counts = { all: allOrders.length, accept: 0, hold: 0, pricechange: 0, pending: 0 };
                  allOrders.forEach(o => { counts[getActionType(o)]++; });

                  const filteredOrders = confirmFilter === 'all'
                    ? allOrders
                    : allOrders.filter(o => getActionType(o) === confirmFilter);

                  const allFilteredIds = filteredOrders.map(o => o._id);
                  const allChecked = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedOrders.includes(id));

                  const toggleAll = () => {
                    if (allChecked) setSelectedOrders(prev => prev.filter(id => !allFilteredIds.includes(id)));
                    else setSelectedOrders(prev => [...new Set([...prev, ...allFilteredIds])]);
                  };
                  const toggleOne = (id) => {
                    setSelectedOrders(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                  };

                  return (
                  <Box sx={{ mt: 3 }}>
                    {/* 헤더 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Typography fontWeight={700} sx={{ color: '#333' }}>
                        가격 확인 현황
                        {confirmStats && (
                          <Chip size="small"
                            label={`${confirmStats.confirmed} / ${confirmStats.total} 응답완료`}
                            sx={{ ml: 1.5, background: confirmStats.confirmed === confirmStats.total && confirmStats.total > 0 ? '#e8f5e9' : '#fff8e1', color: confirmStats.confirmed === confirmStats.total && confirmStats.total > 0 ? '#2e7d32' : '#f57f17', fontWeight: 700, fontSize: 12 }}
                          />
                        )}
                      </Typography>
                      <button className="sm-btn" style={{ fontSize: 12, padding: '4px 10px' }}
                        onClick={() => { fetchConfirmStats(selectedMenu._id); setSelectedOrders([]); }}>
                        새로고침
                      </button>
                    </Box>

                    {confirmStatsLoading ? (
                      <Box sx={{ py: 3, textAlign: 'center', color: '#aaa' }}>로딩 중...</Box>
                    ) : !confirmStats || confirmStats.total === 0 ? (
                      <Box sx={{ py: 3, textAlign: 'center', color: '#aaa', background: 'white', borderRadius: 2, fontSize: 13 }}>
                        발송 후 가격 확인 현황이 표시됩니다.
                      </Box>
                    ) : (
                      <>
                        {/* 필터 탭 */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          {[
                            { key: 'all',         label: `전체 ${counts.all}` },
                            { key: 'accept',      label: `✅ 가격 수락 ${counts.accept}` },
                            { key: 'pricechange', label: `✏️ 수정 요청 ${counts.pricechange}` },
                            { key: 'hold',        label: `⏸ 판매 보류 ${counts.hold}` },
                            { key: 'pending',     label: `⏳ 미확인 ${counts.pending}` },
                          ].map(f => (
                            <Chip key={f.key} size="small" label={f.label} clickable
                              onClick={() => { setConfirmFilter(f.key); setSelectedOrders([]); }}
                              sx={{
                                fontWeight: 700, fontSize: 12,
                                background: confirmFilter === f.key ? '#3f51b5' : '#f0f0f0',
                                color: confirmFilter === f.key ? 'white' : '#555',
                                '&:hover': { background: confirmFilter === f.key ? '#303f9f' : '#e0e0e0' },
                              }}
                            />
                          ))}
                        </Box>

                        {/* 선택 액션 바 */}
                        {selectedOrders.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 2, py: 1.2, background: '#e8eaf6', borderRadius: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#3f51b5', mr: 0.5 }}>
                              {selectedOrders.length}개 선택됨
                            </Typography>
                            <Chip size="small" label="📧 재발송" clickable
                              sx={{ background: '#3f51b5', color: 'white', fontWeight: 700, fontSize: 11 }}
                              onClick={() => {
                                // selectedOrders (order._id) → store._id 매핑
                                const storeIds = selectedOrders
                                  .map(oid => (allOrders.find(o => o._id === oid))?.store?._id)
                                  .filter(Boolean)
                                  .map(String);
                                setSendTableSelectedIds(storeIds);
                                openSendDialog('selected');
                              }}
                            />
                            <Chip size="small" label="✏️ 가격 일괄 변경" clickable
                              sx={{ background: '#e65100', color: 'white', fontWeight: 700, fontSize: 11 }}
                              onClick={() => {
                                // 현재 메뉴 아이템 기반으로 입력 초기화
                                const init = {};
                                (selectedMenu?.items || []).forEach(item => {
                                  init[item.name] = { s: '', l: '', single: '' };
                                });
                                setBulkPriceInputs(init);
                                setBulkPriceOpen(true);
                              }}
                            />
                            <Chip size="small" label="선택 해제" clickable
                              sx={{ background: 'white', color: '#555', fontSize: 11 }}
                              onClick={() => setSelectedOrders([])}
                            />
                          </Box>
                        )}

                        {filteredOrders.length === 0 ? (
                          <Box sx={{ py: 2, textAlign: 'center', color: '#aaa', fontSize: 13 }}>해당 항목이 없습니다.</Box>
                        ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#f0f4ff' }}>
                                <TableCell padding="checkbox">
                                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>점포</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>연락처</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>발송 채널</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>확인 여부</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>확인 일시</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredOrders.map((order) => {
                                const actionType = getActionType(order);
                                const actionChip = ACTION_META[actionType];
                                const isChecked = selectedOrders.includes(order._id);
                                const rowBg = isChecked ? '#e8eaf6' : actionType === 'hold' ? '#fafafa' : actionType === 'pricechange' ? '#fff8f0' : actionType === 'accept' ? '#f1fff4' : 'white';

                                return (
                                  <TableRow key={order._id} hover sx={{ background: rowBg, verticalAlign: 'top' }}>
                                    <TableCell padding="checkbox" sx={{ pt: 1.5 }}>
                                      <input type="checkbox" checked={isChecked} onChange={() => toggleOne(order._id)}
                                        style={{ width: 16, height: 16, cursor: 'pointer' }} />
                                    </TableCell>
                                    <TableCell>
                                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                                        {order.store?.tljNo || order.store?.ownerName || '-'}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#888' }}>{order.store?.address || ''}</div>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, color: '#555' }}>
                                      {order.store?.ownerEmail || order.store?.managerEmail || '-'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip size="small"
                                        label={order.priceNotifyChannel === 'sms' ? 'SMS' : order.priceNotifyChannel === 'both' ? 'Email+SMS' : 'Email'}
                                        sx={{ fontSize: 11, background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                                      />
                                    </TableCell>
                                    {/* 확인 여부 + 가격 변경 상세 (1행 통합) */}
                                    <TableCell>
                                      <Chip size="small" label={actionChip.label}
                                        sx={{ background: actionChip.bg, color: actionChip.color, fontWeight: 700, fontSize: 11 }} />
                                      {actionType === 'pricechange' && (order.itemPrices || []).length > 0 && (
                                        <Box sx={{ mt: 0.8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {(order.itemPrices || []).map((ip, i) => {
                                            const hasSL = ip.price_s != null;
                                            const changed = hasSL
                                              ? (ip.price_s !== ip.finalPrice_s || ip.price_l !== ip.finalPrice_l)
                                              : (ip.basePrice !== ip.finalPrice);
                                            return (
                                              <Box key={i} sx={{ fontSize: 11, background: 'white', border: `1px solid ${changed ? '#ffcc80' : '#c8e6c9'}`, borderRadius: 1, px: 1, py: 0.5 }}>
                                                <span style={{ fontWeight: 700, color: '#333', marginRight: 6 }}>{ip.itemName}</span>
                                                {hasSL ? (
                                                  <>
                                                    <span style={{ color: '#999' }}>HQ S${ip.price_s != null ? (ip.price_s/100).toFixed(2) : '-'}/L${ip.price_l != null ? (ip.price_l/100).toFixed(2) : '-'}</span>
                                                    <span style={{ margin: '0 4px', color: '#bbb' }}>→</span>
                                                    <span style={{ color: changed ? '#e65100' : '#2e7d32', fontWeight: 700 }}>
                                                      S${ip.finalPrice_s != null ? (ip.finalPrice_s/100).toFixed(2) : '-'}/L${ip.finalPrice_l != null ? (ip.finalPrice_l/100).toFixed(2) : '-'}
                                                    </span>
                                                  </>
                                                ) : (
                                                  <>
                                                    <span style={{ color: '#999' }}>HQ ${ip.basePrice != null ? (ip.basePrice/100).toFixed(2) : '-'}</span>
                                                    <span style={{ margin: '0 4px', color: '#bbb' }}>→</span>
                                                    <span style={{ color: changed ? '#e65100' : '#2e7d32', fontWeight: 700 }}>
                                                      ${ip.finalPrice != null ? (ip.finalPrice/100).toFixed(2) : '-'}
                                                    </span>
                                                  </>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, color: '#555' }}>
                                      {order.priceConfirmedAt ? fmtDate(order.priceConfirmedAt) : '-'}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        )}
                      </>
                    )}
                  </Box>
                  );
                })()}
              </Box>
            )}
          </div>
        </div>

        {/* ── DIALOGS ── */}

        {/* Campaign add/edit */}
        <CampaignMenuDialog
          open={menuDialog}
          onClose={() => { setMenuDialog(false); setEditTarget(null); }}
          editTarget={editTarget}
          onSubmit={handleMenuSubmit}
        />

        {/* Order edit */}
        <OrderEditDialog
          open={orderDialog}
          onClose={() => setOrderDialog(false)}
          store={orderDialogStore}
          selectedMenu={selectedMenu}
          existingOrder={orderDialogExisting}
          onSave={handleOrderSave}
        />

        {/* Delete confirm */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>시즌 캠페인 삭제</DialogTitle>
          <DialogContent>
            삭제하면 관련 신청 내역도 모두 삭제됩니다. 계속하시겠습니까?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button
              color="error" variant="contained"
              onClick={() => {
                dispatch(deleteSeasonalMenu(deleteConfirm));
                if (selectedMenu?._id === deleteConfirm) setSelectedMenu(null);
                setDeleteConfirm(null);
              }}
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send confirm */}
        <Dialog open={!!sendConfirm} onClose={() => { if (!isSending) { setSendConfirm(null); setSendResult(null); setIndividualSendStoreId(null); } }} maxWidth="md" fullWidth>
          <DialogTitle sx={{ color: '#551FFF', fontWeight: 700 }}>
            📣 시즌 캠페인 발송
            {sendMode === 'all' && ' · 전체'}
            {sendMode === 'selected' && ' · 선택'}
            {sendMode === 'individual' && ' · 개별'}
          </DialogTitle>
          <DialogContent>
            {sendResult ? (
              /* 발송 결과 */
              <Box>
                {sendResult.error ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="error" fontWeight={700}>{sendResult.error}</Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Typography sx={{ fontSize: 32, mb: 1 }}>✅</Typography>
                    <Typography fontWeight={700} fontSize={16} mb={2}>발송 완료</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ background: '#e8f5e9', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#2e7d32' }}>{sendResult.sent ?? '-'}</div>
                        <div style={{ fontSize: 12, color: '#555' }}>발송 성공</div>
                      </Box>
                      {(sendResult.skipped ?? 0) > 0 && (
                        <Box sx={{ background: '#fff8e1', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#f57f17' }}>{sendResult.skipped}</div>
                          <div style={{ fontSize: 12, color: '#555' }}>스킵 (연락처 없음)</div>
                        </Box>
                      )}
                      {(sendResult.errors ?? 0) > 0 && (
                        <Box sx={{ background: '#ffebee', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#c62828' }}>{sendResult.errors}</div>
                          <div style={{ fontSize: 12, color: '#555' }}>발송 실패</div>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              /* 발송 설정 */
              <Box>
                {/* 대상 점포 요약 */}
                <Box sx={{ background: '#f5f0ff', borderRadius: 2, px: 2, py: 1.5, mb: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#551FFF', mb: 0.5 }}>
                    {sendMode === 'all' && `전체 발송 · 대상 ${activeStoreCount}개 운영 점포`}
                    {sendMode === 'selected' && `선택 발송 · ${sendTableSelectedIds.length}개 점포`}
                    {sendMode === 'individual' && (() => {
                      const st = activeStores.find(s => String(s._id) === individualSendStoreId);
                      return `개별 발송 · ${st ? (st.tljNo || st.ownerName || '점포') : '1개 점포'}`;
                    })()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12, color: '#666' }}>
                      📧 이메일 보유: {activeStores.filter(s => s.ownerEmail || s.managerEmail).length}개
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#666' }}>
                      💬 전화번호 보유: {activeStores.filter(s => s.ownerPhone || s.managerPhone).length}개
                    </Typography>
                  </Box>
                </Box>

                {/* 점포 목록 */}
                {activeStores.length > 0 && (sendMode === 'all' || sendMode === 'selected') && (
                  <Box sx={{ maxHeight: 140, overflowY: 'auto', mb: 2, border: '1px solid #eee', borderRadius: 2 }}>
                    {(sendMode === 'all'
                      ? activeStores
                      : activeStores.filter(s => sendTableSelectedIds.includes(String(s._id)))
                    ).map((s, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.8, borderBottom: '1px solid #f5f5f5', fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{s.tljNo || s.ownerName}</span>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {(s.ownerEmail || s.managerEmail) && <Chip size="small" label="📧" sx={{ fontSize: 10, height: 18, background: '#e3f2fd', color: '#1565c0' }} />}
                          {(s.ownerPhone || s.managerPhone) && <Chip size="small" label="💬" sx={{ fontSize: 10, height: 18, background: '#e8f5e9', color: '#2e7d32' }} />}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                {sendMode === 'individual' && individualSendStoreId && (
                  <Box sx={{ mb: 2, fontSize: 12, color: '#555' }}>
                    {(() => {
                      const st = activeStores.find(s => String(s._id) === individualSendStoreId);
                      if (!st) return null;
                      return (
                        <span>
                          {st.address && <span style={{ color: '#888' }}>{st.address} · </span>}
                          {st.ownerEmail || st.managerEmail || '이메일 없음'}
                        </span>
                      );
                    })()}
                  </Box>
                )}

                {/* 이메일 제목·본문 (채널에 이메일 포함 시) */}
                {(sendChannel === 'email' || sendChannel === 'both') && (
                  <Box sx={{ mb: 2, p: 1.5, background: '#fafafa', borderRadius: 2, border: '1px solid #eee' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: '#333' }}>이메일 내용</Typography>
                    {sendTemplateLoading ? (
                      <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} sx={{ color: '#551FFF' }} /></Box>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          size="small"
                          label="제목"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          sx={{ mb: 1.5 }}
                        />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: '#555' }}>HTML 본문</Typography>
                        <textarea
                          value={emailHtmlDraft}
                          onChange={(e) => setEmailHtmlDraft(e.target.value)}
                          rows={14}
                          spellCheck={false}
                          style={{
                            width: '100%',
                            borderRadius: 8,
                            border: '1px solid #ddd',
                            padding: '10px 12px',
                            fontSize: 12,
                            fontFamily: 'ui-monospace, Menlo, monospace',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                          }}
                        />
                        <Typography sx={{ fontSize: 11, color: '#888', mt: 0.75, lineHeight: 1.5 }}>
                          발송 시 자동 치환: {'{{STORE_NAME}}'}, {'{{ACCEPT_URL}}'}, {'{{HOLD_URL}}'}, {'{{CHANGE_URL}}'},{' '}
                          {'{{MENU_NAME}}'}, {'{{START_STR}}'}, {'{{END_STR}}'} — 링크 태그를 삭제하면 버튼이 동작하지 않습니다.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small" variant="outlined"
                            disabled={!sendConfirm || sendTemplateLoading}
                            onClick={() => sendConfirm && loadSendTemplate(sendConfirm)}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                          >
                            기본 템플릿 다시 불러오기
                          </Button>
                          <Button
                            size="small" variant="contained"
                            disabled={!emailHtmlDraft}
                            onClick={() => setPreviewOpen(true)}
                            sx={{ borderRadius: 2, textTransform: 'none', background: '#333' }}
                          >
                            👁 이메일 미리보기
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {/* 채널 선택 */}
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: '#333' }}>발송 채널</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {[
                    { value: 'email', label: '📧 이메일' },
                    { value: 'sms',   label: '💬 SMS' },
                    { value: 'both',  label: '📧+💬 둘 다' },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setSendChannel(opt.value)}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
                        fontWeight: sendChannel === opt.value ? 700 : 400,
                        background: sendChannel === opt.value ? '#551FFF' : '#f0f0f0',
                        color: sendChannel === opt.value ? 'white' : '#333',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </Box>

                {/* SMS 문구 (SMS or both) */}
                {(sendChannel === 'sms' || sendChannel === 'both') && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, color: '#333' }}>SMS 문구 (선택)</Typography>
                    <textarea
                      rows={3}
                      placeholder={`[TLJ] New item ${sendConfirm ? (menus.find(m=>m._id===sendConfirm)?.name || '') : ''} — Please confirm pricing at the link below.`}
                      value={smsCustomText}
                      onChange={e => setSmsCustomText(e.target.value)}
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #ddd', padding: '8px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                    <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>
                      비워두면 기본 문구 사용 · 링크는 자동 포함됩니다 · 전화번호 없는 점포는 스킵
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {sendResult ? (
              <Button onClick={() => { setSendConfirm(null); setSendResult(null); }} variant="contained" sx={{ background: '#551FFF', borderRadius: 2 }}>
                닫기
              </Button>
            ) : (
              <>
                <Button onClick={() => setSendConfirm(null)} disabled={isSending}>취소</Button>
                <Button variant="contained" disabled={isSending || sendTargetCount === 0 || ((sendChannel === 'email' || sendChannel === 'both') && sendTemplateLoading)}
                  onClick={() => handleSend(sendConfirm)}
                  sx={{ background: '#551FFF', borderRadius: 2, minWidth: 90 }}>
                  {isSending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `발송 (${sendTargetCount}개)`}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* ── 이메일 HTML 미리보기 다이얼로그 ── */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>👁 이메일 미리보기
            <Typography sx={{ fontSize: 12, color: '#aaa', fontWeight: 400, mt: 0.3 }}>
              {'{{STORE_NAME}}'} 등 플레이스홀더는 발송 시 점포별로 자동 치환됩니다.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <iframe
              title="email-preview"
              srcDoc={emailHtmlDraft}
              style={{ width: '100%', height: 600, border: 'none' }}
              sandbox="allow-same-origin"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>닫기</Button>
          </DialogActions>
        </Dialog>

        {/* ── 일괄 가격 변경 다이얼로그 ── */}
        <Dialog open={bulkPriceOpen} onClose={() => { if (!bulkPriceSaving) setBulkPriceOpen(false); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#e65100' }}>
            ✏️ 가격 일괄 변경
            <Typography sx={{ fontSize: 12, color: '#888', fontWeight: 400, mt: 0.3 }}>
              선택된 {selectedOrders.length}개 점포의 확정 가격을 업데이트합니다.
              비워두면 해당 항목은 변경되지 않습니다.
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow sx={{ background: '#fff3e0' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#e65100' }}>항목</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#e65100', textAlign: 'center' }}>현재 HQ가</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#e65100', textAlign: 'center' }}>변경 가격</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedMenu?.items || []).map((item, idx) => {
                  const hasSL = item.price_s != null && item.price_l != null;
                  const inp = bulkPriceInputs[item.name] || {};
                  return (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {item.name}
                        {item.note && <Chip label={item.note} size="small" sx={{ ml: 1, fontSize: 10, height: 18, background: '#e8f5e9', color: '#2e7d32' }} />}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontSize: 13, color: '#555' }}>
                        {hasSL
                          ? `S $${(item.price_s/100).toFixed(2)} / L $${(item.price_l/100).toFixed(2)}`
                          : `$${(item.basePrice/100).toFixed(2)}`
                        }
                      </TableCell>
                      <TableCell>
                        {hasSL ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <TextField size="small" label="S" type="number" sx={{ width: 100 }}
                              value={inp.s || ''}
                              onChange={e => setBulkPriceInputs(prev => ({ ...prev, [item.name]: { ...prev[item.name], s: e.target.value } }))}
                              placeholder={(item.price_s/100).toFixed(2)}
                              InputProps={{ startAdornment: <span style={{fontSize:12,color:'#888',marginRight:2}}>$</span> }}
                              inputProps={{ min: 0, step: 0.05 }}
                            />
                            <TextField size="small" label="L" type="number" sx={{ width: 100 }}
                              value={inp.l || ''}
                              onChange={e => setBulkPriceInputs(prev => ({ ...prev, [item.name]: { ...prev[item.name], l: e.target.value } }))}
                              placeholder={(item.price_l/100).toFixed(2)}
                              InputProps={{ startAdornment: <span style={{fontSize:12,color:'#888',marginRight:2}}>$</span> }}
                              inputProps={{ min: 0, step: 0.05 }}
                            />
                          </Box>
                        ) : (
                          <TextField size="small" label="가격" type="number" sx={{ width: 130 }}
                            value={inp.single || ''}
                            onChange={e => setBulkPriceInputs(prev => ({ ...prev, [item.name]: { ...prev[item.name], single: e.target.value } }))}
                            placeholder={(item.basePrice/100).toFixed(2)}
                            InputProps={{ startAdornment: <span style={{fontSize:12,color:'#888',marginRight:2}}>$</span> }}
                            inputProps={{ min: 0, step: 0.05 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkPriceOpen(false)} disabled={bulkPriceSaving}>취소</Button>
            <Button variant="contained" disabled={bulkPriceSaving}
              sx={{ background: '#e65100', borderRadius: 2, minWidth: 100 }}
              onClick={async () => {
                const itemPrices = (selectedMenu?.items || []).map(item => {
                  const inp = bulkPriceInputs[item.name] || {};
                  const hasSL = item.price_s != null;
                  if (hasSL) {
                    return {
                      itemName: item.name,
                      ...(inp.s ? { finalPrice_s: Math.round(parseFloat(inp.s) * 100) } : {}),
                      ...(inp.l ? { finalPrice_l: Math.round(parseFloat(inp.l) * 100) } : {}),
                    };
                  }
                  return {
                    itemName: item.name,
                    ...(inp.single ? { finalPrice: Math.round(parseFloat(inp.single) * 100) } : {}),
                  };
                }).filter(ip => Object.keys(ip).length > 1);

                if (itemPrices.length === 0) { setBulkPriceOpen(false); return; }
                setBulkPriceSaving(true);
                try {
                  const { api } = await import('../../api/api');
                  await api.patch('/seasonal/bulk-prices', { orderIds: selectedOrders, itemPrices });
                  setBulkPriceOpen(false);
                  setSelectedOrders([]);
                  fetchConfirmStats(selectedMenu._id);
                } catch (err) {
                  alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
                } finally {
                  setBulkPriceSaving(false);
                }
              }}
            >
              {bulkPriceSaving ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `${selectedOrders.length}개 점포 적용`}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}

export default SeasonalMenu;
